// Drawer displaying SCF domain classification for a document.
import {
  Hexagon,
  Info,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { getDomainCoverageColumns } from "./domainCoverageColumns";
import {
  SectionCard,
  PrimaryDomainMatch,
  DomainSummaryOverview,
} from "./domainIntelligenceParts";
import { RuleValidationPanel } from "./ruleValidationPanel";
import {
  DOMAIN_BANNER,
  DOMAIN_DRAWER_WIDTH_CLS,
  DOMAIN_HEADER_COPY,
  DOMAIN_STATUS_PILL,
  DOMAIN_SUMMARY_LABELS,
  DOMAIN_TABLE_MAX_HEIGHT,
  DOMAIN_DRAWER_TABS,
  DOMAIN_RULE_VALIDATION_LAYER,
  DOMAIN_RULE_VALIDATION_OUTCOME,
  DOMAIN_RULE_VALIDATION_SCOPE_KEY,
  DOMAIN_VALIDATION_COPY,
} from "./utils/constants";
import {
  deriveDomainIntel,
  toRuleValidationList,
  type DomainIntelDerived,
} from "./utils/helpers";
import { DataTable } from "../../../components/DataTable";
import { Button } from "../../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import {
  getDomainRuleValidation,
  type DomainClassifyResult,
  type DomainRuleValidation,
} from "../api/contentRegistryApi";
import {
  DOC_TYPE_COLORS,
  DOC_TYPE_FALLBACK_BADGE_CLS,
  DOMAIN_TABLE_PAGE_SIZE,
  LAYER_ICON_BOX_TONE,
} from "../constants";
import { formatDocType, plainText } from "../utils";

export interface DomainIntelligenceDrawerProps {
  open: boolean;
  onClose: () => void;
  result: DomainClassifyResult | null;
  documentTitle?: string;
  documentType?: string;
  frameworkLabel?: string;
  scopeKey?: string | null;
}

export function DomainIntelligenceDrawer({
  open,
  onClose,
  result,
  documentTitle,
  documentType,
  frameworkLabel,
  scopeKey,
}: DomainIntelligenceDrawerProps) {
  const derived = useMemo(
    () => deriveDomainIntel(result, documentTitle),
    [result, documentTitle],
  );
  const columns = useMemo(() => getDomainCoverageColumns(), []);
  const page = useResettablePage(derived.docRefKey);
  const [activeTab, setActiveTab] = useState<keyof typeof DOMAIN_DRAWER_TABS>("overview");
  const [validationRules, setValidationRules] = useState<DomainRuleValidation[]>([]);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);


  const fallbackScopeKey =
    String(derived.primary?.domainCode ?? "").trim() ||
    DOMAIN_RULE_VALIDATION_SCOPE_KEY;
  const effectiveScopeKey = scopeKey?.trim() || fallbackScopeKey;

  useEffect(() => {
    const documentId = result?.document?.id;
    if (!open || !documentId) return;
    const controller = new AbortController();
    let requestActive = true;
    setValidationLoading(true);
    setValidationError(null);
    getDomainRuleValidation(
      documentId,
      effectiveScopeKey,
      DOMAIN_RULE_VALIDATION_LAYER,
      DOMAIN_RULE_VALIDATION_OUTCOME,
      controller.signal,
    )
      .then((payload) => {
        if (requestActive) setValidationRules(toRuleValidationList(payload));
      })
      .catch((error: unknown) => {
        if (!requestActive) return;
        setValidationRules([]);
        setValidationError(error instanceof Error ? error.message : DOMAIN_VALIDATION_COPY.error);
      })
      .finally(() => {
        if (requestActive) setValidationLoading(false);
      });
    return () => {
      requestActive = false;
      controller.abort();
    };
  }, [open, result?.document?.id, effectiveScopeKey]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        overlayClassName="z-[80]"
        className={`z-[80] flex h-full ${DOMAIN_DRAWER_WIDTH_CLS} flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[95vw]`}
      >
        <SheetTitle className="sr-only">{DOMAIN_HEADER_COPY.title}</SheetTitle>
        <SheetDescription className="sr-only">
          {DOMAIN_HEADER_COPY.description}
        </SheetDescription>

        {!result ? (
          <LoadingState />
        ) : (
          <>
            <DrawerHeader
              result={result}
              derived={derived}
              documentTitle={documentTitle}
              documentType={documentType}
              frameworkLabel={frameworkLabel}
            />
            <DrawerBody
              derived={derived}
              columns={columns}
              page={page.value}
              onPageChange={page.set}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              validationRules={validationRules}
              validationLoading={validationLoading}
              validationError={validationError}
              scopeKey={effectiveScopeKey}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// Reset the paginator when the drawer swaps to a different document without
// firing a useEffect (React-recommended derived-state pattern).
function useResettablePage(key: string) {
  const [state, setState] = useState({ key, page: 0 });
  if (state.key !== key) {
    setState({ key, page: 0 });
  }
  return {
    value: state.key === key ? state.page : 0,
    set: (p: number) => setState({ key, page: p }),
  };
}

function LoadingState() {
  return (
    <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="size-5 animate-spin" />
      <span className="text-sm">Loading domain intelligence...</span>
    </div>
  );
}

function DrawerHeader({
  result,
  derived,
  documentTitle,
  documentType,
  frameworkLabel,
}: {
  result: DomainClassifyResult;
  derived: DomainIntelDerived;
  documentTitle?: string;
  documentType?: string;
  frameworkLabel?: string;
}) {
  const docLabel = plainText(documentTitle || result.document?.ref);
  const frameworkText = plainText(
    frameworkLabel || result.taxonomy?.name || DOMAIN_HEADER_COPY.frameworkFallback,
  );
  const showValidatedPill = !derived.stopped && !!result.summary;

  return (
    <header className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border bg-card p-4 pr-14 sm:p-6 sm:pr-16 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div
          className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${LAYER_ICON_BOX_TONE.domain.box}`}
        >
          <Hexagon className={`size-5 ${LAYER_ICON_BOX_TONE.domain.icon}`} />
        </div>
        <div className="min-w-0 flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2 text-lg font-bold text-foreground">
            <span>{DOMAIN_HEADER_COPY.title}</span>
            {docLabel && (
              <>
                <span className="font-normal text-muted-foreground">-</span>
                <span className="break-words">{docLabel}</span>
              </>
            )}
            {documentType && <DocTypeBadge type={documentType} />}
          </div>
          <p className="text-xs text-muted-foreground max-w-xl break-words">
            {DOMAIN_HEADER_COPY.description}
          </p>
          {derived.backendNote && (
            <p className="text-[11px] text-muted-foreground max-w-xl break-words">
              {plainText(derived.backendNote)}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 pt-1">

            <ChipMuted>{DOMAIN_HEADER_COPY.frameworkPrefix}{frameworkText}</ChipMuted>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-[11px] text-muted-foreground">
          {DOMAIN_HEADER_COPY.overallLabel}
        </span>
        <span className="text-3xl font-bold text-emerald-400 tabular-nums">
          {derived.overallPct}
        </span>
      </div>
    </header>
  );
}

function ChipMuted({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-border bg-background/60 px-2 py-0.5 text-[11px] text-foreground break-words">
      {children}
    </span>
  );
}

function DocTypeBadge({ type }: { type: string }) {
  const key = String(plainText(type)).toUpperCase();
  const cls = DOC_TYPE_COLORS[key] ?? DOC_TYPE_FALLBACK_BADGE_CLS;
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${cls}`}
    >
      {formatDocType(key)}
    </span>
  );
}

function StatusPill({ kind }: { kind: "stopped" | "validated" }) {
  const meta = DOMAIN_STATUS_PILL[kind];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}
    >
      <span className={`size-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

function DrawerBody({
  derived,
  columns,
  page,
  onPageChange,
  activeTab,
  onTabChange,
  validationRules,
  validationLoading,
  validationError,
  scopeKey,
}: {
  derived: DomainIntelDerived;
  columns: ReturnType<typeof getDomainCoverageColumns>;
  page: number;
  onPageChange: (p: number) => void;
  activeTab: keyof typeof DOMAIN_DRAWER_TABS;
  onTabChange: (tab: keyof typeof DOMAIN_DRAWER_TABS) => void;
  validationRules: DomainRuleValidation[];
  validationLoading: boolean;
  validationError: string | null;
  scopeKey: string;
}) {
  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-4">
      <div className="flex items-center gap-5 border-b border-border px-1">
        {(Object.entries(DOMAIN_DRAWER_TABS) as Array<[keyof typeof DOMAIN_DRAWER_TABS, string]>).map(([key, label]) => (
          <Button
            key={key}
            type="button"
            variant="ghost"
            size="none"
            onClick={() => onTabChange(key)}
            className={`border-b-2 px-2 pb-2 text-xs font-semibold hover:bg-transparent dark:hover:bg-transparent ${activeTab === key
                ? "border-indigo-500 text-indigo-600 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-400"
                : "border-transparent text-muted-foreground hover:text-muted-foreground"
              }`}
          >
            {label}
          </Button>
        ))}
      </div>
      {derived.hasBanner && <WarningBanners derived={derived} />}
      {activeTab === "overview" ? (
        <>
          <DomainSummaryOverview derived={derived} />
          <SectionCard headerLabel={DOMAIN_SUMMARY_LABELS.primary} rightLabel={DOMAIN_SUMMARY_LABELS.primaryRight}>
            <PrimaryDomainMatch primary={derived.primary} />
          </SectionCard>
          <SectionCard headerLabel={DOMAIN_SUMMARY_LABELS.all} rightLabel={`${derived.matchedCoverage.length} ${DOMAIN_SUMMARY_LABELS.allRightSuffix}`}>
            <CoverageTable derived={derived} columns={columns} page={page} onPageChange={onPageChange} />
          </SectionCard>
        </>
      ) : (
        <RuleValidationPanel
          rules={validationRules}
          loading={validationLoading}
          error={validationError}
          scopeKey={scopeKey}
        />
      )}
    </div>
  );
}

function CoverageTable({
  derived,
  columns,
  page,
  onPageChange,
}: {
  derived: DomainIntelDerived;
  columns: ReturnType<typeof getDomainCoverageColumns>;
  page: number;
  onPageChange: (p: number) => void;
}) {
  if (derived.matchedCoverage.length === 0) {
    return (
      <div className="p-3">
        <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
          <Info className="size-4" />
          {DOMAIN_SUMMARY_LABELS.emptyCoverage}
        </div>
      </div>
    );
  }

  const needsPager = derived.matchedCoverage.length > DOMAIN_TABLE_PAGE_SIZE;

  return (
    <div className="p-3">
      <DataTable
        columns={columns}
        data={derived.matchedCoverage}
        compact
        maxHeight={DOMAIN_TABLE_MAX_HEIGHT}
        rowKey={(row) => row.domainCode}
        {...(needsPager
          ? {
            page,
            pageSize: DOMAIN_TABLE_PAGE_SIZE,
            totalItems: derived.matchedCoverage.length,
            onPageChange,
            itemLabel: "domains",
            pagerStyle: "simple" as const,
          }
          : {})}
      />
    </div>
  );
}

function WarningBanners({ derived }: { derived: DomainIntelDerived }) {
  return (
    <div className="flex flex-col gap-2">
      {derived.stopped && (
        <Banner title={DOMAIN_BANNER.stopped.title} body={DOMAIN_BANNER.stopped.body} />
      )}
      {derived.zeroRequirements && (
        <Banner
          title={DOMAIN_BANNER.zeroRequirements.title}
          body={DOMAIN_BANNER.zeroRequirements.body}
        />
      )}
      {derived.warnings.length > 0 && (
        <Banner title={DOMAIN_BANNER.taxonomyWarnings.title}>
          <ul className="mt-1 list-disc pl-4 opacity-90">
            {derived.warnings.map((w, i) => (
              <li key={i} className="break-words">
                {plainText(w)}
              </li>
            ))}
          </ul>
        </Banner>
      )}
    </div>
  );
}

function Banner({
  title,
  body,
  children,
}: {
  title: string;
  body?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-[12px] text-amber-300">
      <AlertTriangle className="mt-0.5 size-4 flex-none" />
      <div className="min-w-0 break-words">
        <div className="font-semibold text-amber-200">{title}</div>
        {body && <div className="mt-0.5 opacity-90">{body}</div>}
        {children}
      </div>
    </div>
  );
}

